/* 30 khách mẫu lưu trên máy (không có trên server) — đủ tình huống cho 4 góc nhìn */
import dayjs from 'dayjs';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import type { CareStatusItem } from '../../RoomChat/chatLabels';
import { BRANCHES } from '../dashboard/data';
import {
  EMPTY_LOCAL, HOUR, MIN, MISSED, findTag, uid, type ApptSt, type DichVu, type Kenh, type LeadAppt, type LeadLocal, type Mang, type Outcome, type Touch,
} from './logic';
import { PROMOS, QUOTE_SVC, depositNote, type Deposit, type DepositCustomer, type Quote, type QuoteSt } from './quote';
import { orderFromQuote } from './deposit';

/** id khách mẫu bắt đầu từ đây để không trùng id thật */
export const DEMO_ID_BASE = 9_000_000;
export const isDemoId = (id: number) => id > DEMO_ID_BASE;

export type DemoCustomer = ChannelCustomerItem & { demo: true; tag_code: string };
export interface Staff { id: number; name: string }

const PAGES = [
  { source: 'facebook', page: 'Fanpage Newhair' },
  { source: 'facebook', page: 'Fanpage Khơ Thị Clinic' },
  { source: 'tiktok', page: 'TikTok Khơ Thị PTTM' },
  { source: 'zalo', page: 'Zalo OA Khơ Thị' },
];
const PROVINCES = ['Hồ Chí Minh', 'Đồng Nai', 'Bình Dương', 'Long An', 'Cần Thơ', 'Tiền Giang', 'Đồng Tháp', 'Bà Rịa - Vũng Tàu'];
const NOTES: Record<number, string[]> = {
  0: ['Rụng tóc đỉnh đầu 2 năm, hỏi giá cấy', 'Tóc thưa vùng trán, muốn tư vấn Regen', 'Hói chữ M, đã dùng thuốc thoa 1 năm', 'Rụng tóc sau sinh, hỏi có cần cấy không'],
  1: ['Hỏi trị nám má, da sạm', 'Muốn nâng cơ trẻ hoá, 40 tuổi', 'Mụn ẩn và thâm, hỏi phác đồ', 'Hỏi tiêm làm đầy rãnh cười'],
  2: ['Hỏi cắt mí, cưới tháng 12', 'Muốn nâng mũi, sợ đau', 'Hỏi hút mỡ bụng sau sinh'],
  3: ['Bạn giới thiệu, hỏi chung về dịch vụ'],
};
const PAGE_MANG: Mang[] = ['Newhair', 'Clinic', 'PTTM', 'Partnership'];
const SERVICE: Record<Mang, string> = {
  Newhair: 'Giải pháp Newhair · cấy tóc', Clinic: 'Phác đồ K·Lift', PTTM: 'Gói phẫu thuật theo độ tuổi', Partnership: 'Theo mảng dịch vụ khách cần',
};

interface ApptSpec { at: number; branch: number; status: ApptSt; consultant?: string; outcome?: Outcome['kind']; amount?: number }
interface Spec {
  name: string;
  gender: 1 | 2;
  page: number;
  tag: string;
  ago: number;
  /** chỉ số Telesales trong danh sách; null = chưa phân */
  tele: number | null;
  calls?: [number, string, string?][];
  /** Khách gọi đến bị nhỡ N phút trước, chưa gọi lại */
  missed?: number;
  callback?: number;
  appt?: ApptSpec;
  kenh?: Kenh;
  msgs?: string[];
  /** Hội thoại chat: [phút trước, k = khách / n = nhân viên, nội dung] */
  chat?: [number, 'k' | 'n', string][];
}

export function buildDemo(now: number, teles: Staff[], pages: Staff[]): { customers: DemoCustomer[]; locals: Record<number, LeadLocal> } {
  const q = (t: number) => Math.round(t / (15 * MIN)) * 15 * MIN;
  const d = (days: number, h: number, m = 0) => dayjs(now).add(days, 'day').hour(h).minute(m).second(0).millisecond(0).valueOf();
  const T = (i: number | null) => (i === null ? null : teles[i % teles.length]);

  const specs: Spec[] = [
    // Khách mới từ Trực page, chưa gọi (hạn 15 phút)
    { name: 'Nguyễn Thị Hồng Nhung', gender: 2, page: 0, tag: 'Mới', ago: 6 * MIN, tele: 0 },
    { name: 'Trần Minh Khang', gender: 1, page: 2, tag: 'Mới', ago: 12 * MIN, tele: 1, chat: [[11, 'k', 'Cho em hỏi cắt mí giá bao nhiêu ạ?'], [3, 'k', 'Shop ơi']] },
    { name: 'Lê Thu Trang', gender: 2, page: 1, tag: 'Mới', ago: 25 * MIN, tele: 0, chat: [[24, 'k', 'Em bị nám má, bên mình có phác đồ không?'], [20, 'n', 'Dạ có ạ, chị cho em xin SĐT để tư vấn kỹ hơn nhé'], [12, 'k', 'Số em 093... nha']] },
    { name: 'Phạm Quốc Bảo', gender: 1, page: 0, tag: 'Mới', ago: 48 * MIN, tele: 2 },
    { name: 'Võ Ngọc Hân', gender: 2, page: 3, tag: 'Mới', ago: 2 * HOUR, tele: 1 },
    // Gọi không được → gọi lại sau 2 giờ
    { name: 'Đặng Văn Toàn', gender: 1, page: 0, tag: 'S0', ago: 26 * HOUR, tele: 0, calls: [[3 * HOUR, 'KK-01']] },
    { name: 'Huỳnh Thị Mai Anh', gender: 2, page: 1, tag: 'S0', ago: 22 * HOUR, tele: 2, calls: [[50 * MIN, 'KK-02']], missed: 9 },
    { name: 'Bùi Đức Huy', gender: 1, page: 2, tag: 'S0', ago: 50 * HOUR, tele: 0, calls: [[5 * HOUR, 'KK-03'], [26 * HOUR, 'KK-01']] },
    // Hẹn gọi lại
    { name: 'Đỗ Thanh Tâm', gender: 2, page: 0, tag: 'S2', ago: 48 * HOUR, tele: 1, chat: [[45, 'k', 'Chiều nay em rảnh, gọi em nhé']], calls: [[24 * HOUR, 'KN-02', 'Khách đang bận họp, hẹn gọi lại']], callback: q(now + 30 * MIN) },
    { name: 'Ngô Gia Hưng', gender: 1, page: 1, tag: 'S2', ago: 72 * HOUR, tele: 0, calls: [[20 * HOUR, 'KN-02']], callback: q(now - 40 * MIN) },
    { name: 'Dương Thị Kim Ngân', gender: 2, page: 2, tag: 'S3', ago: 70 * HOUR, tele: 2, calls: [[22 * HOUR, 'KN-02', 'Hỏi lại chồng rồi báo']], callback: q(now + 150 * MIN) },
    { name: 'Lý Hoàng Nam', gender: 1, page: 0, tag: 'S3', ago: 96 * HOUR, tele: 0, calls: [[25 * HOUR, 'KN-03', 'Lo chi phí']], callback: d(1, 10) },
    { name: 'Phan Thị Thanh Hương', gender: 2, page: 1, tag: 'S4', ago: 120 * HOUR, tele: 1, chat: [[30, 'k', 'Báo giá có gồm thuốc chưa em?'], [26, 'n', 'Dạ đã gồm thuốc bôi sau làm ạ']], calls: [[48 * HOUR, 'KN-04']], callback: q(now + 4 * HOUR) },
    { name: 'Trương Công Danh', gender: 1, page: 0, tag: 'S4', ago: 140 * HOUR, tele: 2, calls: [[26 * HOUR, 'KN-04', 'Đã gửi báo giá Regen']], missed: 24 },
    { name: 'Hồ Bảo Ngọc', gender: 2, page: 3, tag: 'S1', ago: 50 * HOUR, tele: 0, kenh: 'Chỉ nhắn tin', msgs: ['Em ở nhà có con nhỏ, nhắn Zalo giúp em nha'] },
    // Đã đặt lịch
    { name: 'Mai Xuân Phúc', gender: 1, page: 0, tag: 'S5', ago: 48 * HOUR, tele: 0, calls: [[24 * HOUR, 'KN-01']], appt: { at: q(now + 60 * MIN), branch: 0, status: 'booked' } },
    { name: 'Vũ Thị Lan', gender: 2, page: 1, tag: 'S5', ago: 72 * HOUR, tele: 1, calls: [[30 * HOUR, 'KN-01']], appt: { at: q(now + 3 * HOUR), branch: 0, status: 'confirmed' } },
    { name: 'Tạ Minh Triết', gender: 1, page: 2, tag: 'S5', ago: 50 * HOUR, tele: 2, calls: [[20 * HOUR, 'KN-01']], appt: { at: q(now + 2 * HOUR), branch: 0, status: 'booked' } },
    { name: 'Châu Ngọc Diễm', gender: 2, page: 0, tag: 'S5', ago: 26 * HOUR, tele: 0, calls: [[5 * HOUR, 'KN-01']], appt: { at: d(1, 9, 30), branch: 0, status: 'booked' } },
    { name: 'La Quang Vinh', gender: 1, page: 1, tag: 'S5', ago: 96 * HOUR, tele: 1, calls: [[48 * HOUR, 'KN-01']], appt: { at: d(1, 14), branch: 0, status: 'confirmed' } },
    // Khách đến cơ sở hôm nay
    { name: 'Cao Thị Yến', gender: 2, page: 0, tag: 'S6', ago: 72 * HOUR, tele: 2, calls: [[40 * HOUR, 'KN-01']], appt: { at: q(now - 30 * MIN), branch: 0, status: 'confirmed' } },
    { name: 'Lâm Tuấn Kiệt', gender: 1, page: 2, tag: 'S6', ago: 120 * HOUR, tele: 0, calls: [[50 * HOUR, 'KN-01']], appt: { at: q(now - 90 * MIN), branch: 0, status: 'arrived', consultant: 'Hoàng Mai Linh' } },
    { name: 'Kiều Mỹ Duyên', gender: 2, page: 1, tag: 'S6', ago: 140 * HOUR, tele: 1, calls: [[60 * HOUR, 'KN-01']], appt: { at: q(now - 150 * MIN), branch: 0, status: 'arrived' } },
    { name: 'Thái Văn Lộc', gender: 1, page: 0, tag: 'S4', ago: 160 * HOUR, tele: 2, calls: [[70 * HOUR, 'KN-01']], appt: { at: q(now - 4 * HOUR), branch: 0, status: 'done', consultant: 'Bùi Đức Tâm', outcome: 'think' }, callback: d(1, 10) },
    // Không đến / rớt
    { name: 'Quách Thu Hà', gender: 2, page: 1, tag: 'S5.1', ago: 120 * HOUR, tele: 0, calls: [[50 * HOUR, 'KN-01']], appt: { at: d(-1, 15), branch: 0, status: 'noshow' }, callback: q(now - 2 * HOUR) },
    { name: 'Tôn Nữ Minh Thư', gender: 2, page: 2, tag: 'KNC', ago: 50 * HOUR, tele: 1, calls: [[24 * HOUR, 'KN-09', 'Chỉ hỏi giá tham khảo']] },
    { name: 'Hà Gia Bảo', gender: 1, page: 1, tag: 'S6.1', ago: 150 * HOUR, tele: 2, calls: [[80 * HOUR, 'KN-01']], appt: { at: d(-1, 10), branch: 0, status: 'done', consultant: 'Đỗ Quang Huy', outcome: 'no_need' } },
    // Chưa phân Telesales
    { name: 'Đinh Thị Thảo', gender: 2, page: 0, tag: 'Mới', ago: 8 * MIN, tele: null },
    { name: 'Lương Văn Sang', gender: 1, page: 1, tag: 'Mới', ago: 35 * MIN, tele: null, chat: [[33, 'k', 'Tiêm làm đầy rãnh cười có đau không ạ?'], [18, 'k', '?']] },
    { name: 'Triệu Ngọc Ánh', gender: 2, page: 2, tag: 'S0', ago: 3 * HOUR, tele: null },
  ];

  const customers: DemoCustomer[] = [];
  const locals: Record<number, LeadLocal> = {};
  const seq: Record<string, number> = {};
  specs.forEach((s, i) => {
    const id = DEMO_ID_BASE + i + 1;
    const created = q(now - s.ago);
    const tele = T(s.tele);
    const pageStaff = pages[i % pages.length];
    const src = PAGES[s.page];
    const mang = PAGE_MANG[s.page];
    const phone = `09${String(10000000 + ((i + 1) * 7919301) % 89999999).slice(0, 8)}`;
    const code = `KH${800100 + i}`;
    customers.push({
      id,
      demo: true,
      tag_code: s.tag,
      display_name: s.name,
      fullname: s.name,
      phone,
      customer_code: code,
      gender: s.gender,
      province: { code: '', name: PROVINCES[i % PROVINCES.length], full_name: PROVINCES[i % PROVINCES.length] },
      created_at: new Date(created).toISOString(),
      updated_at: new Date(created).toISOString(),
      note: NOTES[s.page][i % NOTES[s.page].length],
      source: src.source,
      connections: [{ id, source: src.source, channel_connection: { id: 0, display_name: src.page, channel_type_code: src.source } }],
      contact_staff: tele,
      telesale: tele,
      in_charge_staff: pageStaff,
      staff: pageStaff,
    } as unknown as DemoCustomer);

    const by = tele?.name ?? 'Telesales';
    const touches: Touch[] = (s.calls ?? []).map(([ago, c, note]) => ({
      id: uid(), at: q(now - ago), kind: 'call', dir: 'out', dur: c.startsWith('KN') ? 90 + ((i * 37) % 200) : 0, code: c, text: note, by,
    }));
    if (s.missed) touches.push({ id: uid(), at: now - s.missed * MIN, kind: 'call', dir: 'in', dur: 0, code: MISSED, by: 'Tổng đài' });
    const appts: LeadAppt[] = [];
    if (s.appt) {
      const a = s.appt;
      const outcome: Outcome | undefined = a.outcome ? { kind: a.outcome, note: a.outcome === 'think' ? 'Cần bàn thêm với gia đình' : 'Khách chỉ muốn tham khảo', by: a.consultant ?? 'Tư vấn viên', at: a.at + HOUR } : undefined;
      appts.push({
        id: uid(), at: a.at, branch: BRANCHES[a.branch].name, mang, service: SERVICE[mang],
        note: NOTES[s.page][i % NOTES[s.page].length], status: a.status, by, createdAt: touches[0]?.at ?? created,
        cust: { name: s.name, phone, code }, consultant: a.consultant, outcome,
      });
      touches.unshift({ id: uid(), at: touches[0]?.at ?? created, kind: 'appt', text: `Đặt lịch ${dayjs(a.at).format('HH:mm DD/MM')} · ${BRANCHES[a.branch].name} · ${mang}`, by });
    }
    touches.sort((x, y) => y.at - x.at);
    const dq = demoQuotes(s.name, now, by, { id, name: s.name, code, phone }, seq);
    locals[id] = {
      ...EMPTY_LOCAL,
      mang: s.page === 3 ? 'Partnership' : undefined,
      kenh: s.kenh,
      callback: s.callback,
      touches,
      appts,
      msgs: [
        ...(s.msgs ?? []).map((text) => ({ id: uid(), at: created + 2 * MIN, from: 'k' as const, text })),
        ...(s.chat ?? []).map(([m, from, text]) => ({ id: uid(), at: now - m * MIN, from, text, by: from === 'n' ? pageStaff?.name : undefined })),
      ],
      quotes: dq,
    };
  });
  return { customers, locals };
}

/** Lịch sử báo giá mẫu: [giờ trước, dịch vụ, chỉ số gói, % giảm, trạng thái, hạn ưu đãi (ngày từ nay), cọc, lý do giảm, đã đẩy POS] */
type QSpec = [number, DichVu, number[], number, QuoteSt, number, Deposit['status']?, string?, boolean?];
const DEMO_QUOTES: Record<string, QSpec[]> = {
  'Trương Công Danh': [
    [26 * HOUR, 'Cấy tóc', [1], 0, 'sent', 3, 'waiting'],
    [80 * HOUR, 'Cấy tóc', [0], 0, 'replaced', 3],
  ],
  'Phan Thị Thanh Hương': [[48 * HOUR, 'Điều trị da', [0, 3], 5, 'replied', 12, 'checking', 'Khách cũ giới thiệu']],
  'Lý Hoàng Nam': [[2 * HOUR, 'Cấy tóc', [0], 10, 'pending', 20, undefined, 'Khách lo chi phí, so sánh nơi khác']],
  'Thái Văn Lộc': [[150 * HOUR, 'Căng chỉ', [1], 0, 'sent', -2]],
  'Tôn Nữ Minh Thư': [[24 * HOUR, 'Phun xăm', [0, 1], 0, 'cancelled', 10]],
  // Đã nhận cọc: 1 khách đã đẩy qua POS (có đơn hàng cọc), 1 khách chờ đẩy
  'Vũ Thị Lan': [[30 * HOUR, 'Điều trị da', [0], 0, 'sent', 10, 'received', undefined, true]],
  'Kiều Mỹ Duyên': [[60 * HOUR, 'Cấy tóc', [0], 0, 'replied', 5, 'received']],
  'Hà Gia Bảo': [[80 * HOUR, 'Căng chỉ', [1], 0, 'sent', -1, 'forfeited']],
};
const ACCOUNTANT = 'Kế toán Ngọc';
/** Số thứ tự mã theo ngày, chung mọi khách (BG / CO / DH-MMDD-NN) */
const nextCode = (seq: Record<string, number>, prefix: string, at: number) => {
  const key = `${prefix}-${dayjs(at).format('MMDD')}`;
  seq[key] = (seq[key] ?? 0) + 1;
  return `${key}-${String(seq[key]).padStart(2, '0')}`;
};
function demoQuotes(name: string, now: number, by: string, cust: DepositCustomer, seq: Record<string, number>): Quote[] | undefined {
  const specs = DEMO_QUOTES[name];
  if (!specs) return undefined;
  const list: Quote[] = specs.map(([ago, dv, idx, discount, status, valid, dep, reason, pushed], k) => {
    const at = now - ago;
    const code = nextCode(seq, 'BG', at);
    const quote: Quote = {
      id: uid(), code, at, by, dichVu: dv, items: idx.map((i) => QUOTE_SVC[dv][i]), discount, reason,
      promo: k === 0 && dv !== 'Phun xăm' ? PROMOS[0] : undefined,
      channel: 'Zalo', sendAt: at + 5 * MIN, validUntil: dayjs(now).add(valid, 'day').endOf('day').valueOf(), status,
      cancelReason: status === 'cancelled' ? 'Khách chỉ hỏi giá tham khảo' : undefined,
    };
    if (!dep) return quote;
    const got = dep === 'received' || dep === 'forfeited';
    const receivedAt = at + 3 * HOUR;
    const deposit: Deposit = {
      code: nextCode(seq, 'CO', at), amount: 1_000_000, purpose: 'Giữ lịch', due: dayjs(at).add(2, 'day').endOf('day').valueOf(), status: dep,
      note: depositNote(cust.code, code), cust, by, createdAt: at,
      ...(dep !== 'waiting' ? { receiptAt: at + 2 * HOUR } : {}),
      ...(got ? { receivedAt, receivedBy: ACCOUNTANT, bankRef: `FT${dayjs(receivedAt).format('YYMMDD')}${String(cust.id).slice(-5)}` } : {}),
      ...(dep === 'forfeited' ? { closedAt: now - 2 * HOUR, closedBy: ACCOUNTANT, closeReason: 'Không đến lịch, quá hạn giữ chỗ theo chính sách cọc' } : {}),
    };
    if (pushed) deposit.order = orderFromQuote(quote, deposit, nextCode(seq, 'DH', receivedAt + HOUR), ACCOUNTANT, receivedAt + HOUR);
    return { ...quote, deposit };
  });
  const latest = list.find((q) => q.status === 'sent');
  return list.map((q) => (q.status === 'replaced' && latest ? { ...q, replacedBy: latest.code } : q));
}

/** Gắn mã S và thẻ chat thật (theo tên) cho khách mẫu để hiển thị như khách từ API */
export const enrichDemo = (customers: DemoCustomer[], tags: CustomerTagItem[], careStatuses: CareStatusItem[]): DemoCustomer[] =>
  customers.map((c) => {
    const t = findTag(tags, c.tag_code);
    const care = demoChatTagNames(c)
      .map((n) => careStatuses.find((x) => x.name === n))
      .filter((x): x is CareStatusItem => !!x)
      .map((x) => ({ id: x.id, name: x.name, color: x.color }));
    return { ...c, customer_tag: t ? { id: t.id, name: t.name, color: t.color } : { id: -1, name: c.tag_code, color: '#64748b' }, care_statuses: care };
  });

/** Thẻ chat của khách mẫu theo trang nguồn (tên thẻ có trong danh mục Thẻ chat) */
const PAGE_CHAT_TAGS = [['New Hairs'], ['Nâng Cơ', 'DV Da'], ['PTTM'], ['CTV']];
export const demoChatTagNames = (c: DemoCustomer) => {
  const i = c.id - DEMO_ID_BASE - 1;
  const p = Math.max(0, PAGES.findIndex((x) => x.page === c.connections?.[0]?.channel_connection?.display_name));
  const own = PAGE_CHAT_TAGS[p];
  return [
    c.tag_code === 'Mới' ? 'Data Mới' : 'Để lại số',
    own[i % own.length],
    ...(c.connections?.[0]?.source === 'facebook' && i % 3 === 0 ? ['Link FB'] : []),
    ...(c.tag_code === 'KNC' ? ['Không Đạt'] : []),
  ];
};
